
import React, { useState, useEffect, useRef } from 'react';
import { ChatMessage, Player } from '../types';
import { isStaff } from '../utils/roles';

interface ChatProps {
  messages: ChatMessage[];
  onSendMessage: (text: string) => void;
  onClearChat: () => void;
  currentUser: Player;
  roomName: string;
}

const Chat: React.FC<ChatProps> = ({ messages, onSendMessage, onClearChat, currentUser, roomName }) => {
  const [inputValue, setInputValue] = useState('');
  const [isMinimized, setIsMinimized] = useState(window.innerWidth < 768);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const isGuardian = isStaff(currentUser.role);

  useEffect(() => {
    if (!isMinimized) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isMinimized]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onSendMessage(inputValue);
      setInputValue('');
    }
  };

  if (isMinimized) {
    return (
      <button 
        onClick={() => setIsMinimized(false)}
        className="bg-indigo-950/90 border border-indigo-400/50 p-2 md:p-4 rounded-full shadow-2xl backdrop-blur-md flex items-center gap-2 md:gap-3 hover:scale-105 transition-all text-yellow-400 font-bold text-[10px] md:text-sm z-[150]"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
        Chat
      </button>
    );
  }

  return (
    <div className="w-full max-w-[280px] md:max-w-[320px] h-48 md:h-[450px] flex flex-col bg-indigo-950/95 border border-indigo-400/30 rounded-t-2xl overflow-hidden backdrop-blur-xl shadow-2xl z-[150] animate-fade-in">
      <div className="p-2 md:p-4 bg-indigo-900/60 border-b border-indigo-400/20 text-yellow-400 font-bold flex items-center justify-between">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2 text-[10px] md:text-sm">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            Akademi-Chat
          </div>
          <div className="text-[7px] md:text-[8px] uppercase tracking-widest text-indigo-300 opacity-60 truncate">
            {roomName}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isGuardian && (
            <button 
              onClick={(e) => { e.stopPropagation(); if(confirm('Vil du rydde alle beskeder i dette rum?')) onClearChat(); }}
              className="text-indigo-400 hover:text-red-400 transition-colors p-1"
              title="Ryd Chat"
            >
              <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
            </button>
          )}
          <button 
            onClick={() => setIsMinimized(true)}
            className="text-indigo-300 hover:text-white transition-colors p-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-2 md:p-4 space-y-2 md:space-y-4 no-scrollbar bg-black/20">
        {messages.map((msg) => {
          const isMe = msg.senderId === currentUser.id;
          const isSystem = msg.senderId === 'system';
          
          if (isSystem) {
            return (
              <div key={msg.id} className="flex justify-center">
                <div className="bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-[8px] md:text-[10px] text-indigo-300 italic text-center">
                  {msg.text}
                </div>
              </div>
            );
          }

          return (
            <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} animate-fade-in`}>
              <span className="text-[7px] md:text-[10px] text-indigo-400 px-1 mb-0.5 font-bold tracking-wider uppercase">{msg.senderName}</span>
              <div className={`max-w-[90%] px-2 py-1.5 rounded-xl text-[10px] md:text-sm shadow-md ${
                isMe ? 'bg-indigo-600 text-white rounded-tr-none shadow-indigo-900/40' : 'bg-gray-800/80 text-gray-200 rounded-tl-none border border-white/5'
              }`}>
                {msg.text}
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-2 bg-black/40 border-t border-white/5 flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Hvisk til rummet..."
          className="flex-1 bg-black/40 border border-indigo-400/20 rounded-lg px-2 py-1 text-white text-[10px] md:text-sm focus:outline-none focus:border-yellow-400/50 transition-all placeholder:text-indigo-400/30"
        />
        <button type="submit" className="bg-gradient-to-r from-yellow-600 to-amber-600 text-white px-3 py-1 rounded-lg text-[10px] md:text-sm font-bold active:scale-95 transition-transform">
          OK
        </button>
      </form>
    </div>
  );
};

export default Chat;
